import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { testApiKey } from '../services/api'
import type { AIModel } from '@shared/types'

interface APIConfig {
  model: AIModel
  apiKey: string
  status: 'untested' | 'testing' | 'success' | 'error'
  message: string
}

const MODEL_INFO = {
  openai: { label: 'OpenAI', description: 'GPT-4 Turbo', placeholder: 'sk-... (可选，留空使用后台默认配置)' },
  deepseek: { label: 'DeepSeek', description: 'DeepSeek Chat', placeholder: 'sk-... (可选，留空使用后台默认配置)' },
  doubao: { label: '豆包', description: 'Doubao Pro', placeholder: 'Bearer token (可选，留空使用后台默认配置)' },
  qwen: { label: '千问', description: 'Qwen Turbo', placeholder: 'sk-... (可选，留空使用后台默认配置)' }
}

export default function APIConfigPage() {
  const navigate = useNavigate()
  const [configs, setConfigs] = useState<Record<AIModel, APIConfig>>(() => {
    // 从 localStorage 加载配置
    const saved = localStorage.getItem('api_configs')
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        // 如果解析失败，返回默认配置
      }
    }

    // 默认配置
    return {
      openai: { model: 'openai', apiKey: '', status: 'untested', message: '' },
      deepseek: { model: 'deepseek', apiKey: '', status: 'untested', message: '' },
      doubao: { model: 'doubao', apiKey: '', status: 'untested', message: '' },
      qwen: { model: 'qwen', apiKey: '', status: 'untested', message: '' }
    }
  })

  // 保存配置到 localStorage
  const saveConfigs = (newConfigs: Record<AIModel, APIConfig>) => {
    setConfigs(newConfigs)
    localStorage.setItem('api_configs', JSON.stringify(newConfigs))
  }

  // 更新 API Key
  const handleApiKeyChange = (model: AIModel, apiKey: string) => {
    const newConfigs = {
      ...configs,
      [model]: {
        ...configs[model],
        apiKey,
        status: 'untested' as const,
        message: ''
      }
    }
    saveConfigs(newConfigs)
  }

  // 测试 API（留空则测试后台默认配置）
  const handleTest = async (model: AIModel) => {
    const config = configs[model]

    // 设置测试中状态
    const testingMessage = config.apiKey.trim()
      ? '测试用户自定义 API...'
      : '测试后台默认配置...'

    const newConfigs = {
      ...configs,
      [model]: { ...config, status: 'testing' as const, message: testingMessage }
    }
    setConfigs(newConfigs)

    // 测试 API（如果 apiKey 为空，后端会使用默认配置）
    const result = await testApiKey(config.apiKey || undefined, model)

    // 更新状态
    const finalConfigs = {
      ...configs,
      [model]: {
        ...config,
        status: result.success ? 'success' as const : 'error' as const,
        message: result.message
      }
    }
    saveConfigs(finalConfigs)
  }

  // 清除配置
  const handleClear = (model: AIModel) => {
    const newConfigs = {
      ...configs,
      [model]: { model, apiKey: '', status: 'untested' as const, message: '' }
    }
    saveConfigs(newConfigs)
  }

  // 获取状态指示器
  const getStatusIndicator = (config: APIConfig) => {
    switch (config.status) {
      case 'testing':
        return (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <div className="w-3 h-3 rounded-full bg-blue-600 animate-pulse"></div>
            <span>测试中...</span>
          </div>
        )
      case 'success':
        return (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <div className="w-3 h-3 rounded-full bg-green-600"></div>
            <span>✓ {config.message}</span>
          </div>
        )
      case 'error':
        return (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <div className="w-3 h-3 rounded-full bg-red-600"></div>
            <span>✗ {config.message}</span>
          </div>
        )
      default:
        return (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <div className="w-3 h-3 rounded-full bg-gray-300"></div>
            <span>未测试</span>
          </div>
        )
    }
  }

  // 统计已配置的 API 数量（用户自定义 + 测试成功的）
  const configuredCount = Object.values(configs).filter(c => c.status === 'success').length

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">API 配置中心</h1>
          <p className="text-gray-600 mt-2">
            配置和测试各个 AI 服务商的 API 密钥（可选）
          </p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="btn btn-secondary"
        >
          返回首页
        </button>
      </div>

      {/* 重要提示 */}
      <div className="card bg-blue-50 border-2 border-blue-200">
        <div className="flex items-start gap-3">
          <div className="text-2xl">ℹ️</div>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-2">💡 新功能：支持后台默认配置</h3>
            <div className="text-sm text-blue-800 space-y-1">
              <p>• <strong>留空输入框</strong>：系统会自动使用后台配置的默认 API（推荐）</p>
              <p>• <strong>自定义 API</strong>：输入您自己的 API Key 使用专属配置</p>
              <p>• 已配置数量：<strong className="text-lg">{configuredCount} / 4</strong></p>
            </div>
          </div>
        </div>
      </div>

      {/* API 配置列表 */}
      <div className="space-y-4">
        {(Object.keys(configs) as AIModel[]).map((model) => {
          const config = configs[model]
          const info = MODEL_INFO[model]
          const isUsingCustom = config.apiKey.trim() !== ''

          return (
            <div key={model} className="card">
              <div className="space-y-4">
                {/* 标题行 */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-gray-900">{info.label}</h3>
                      {!isUsingCustom && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          后台默认
                        </span>
                      )}
                      {isUsingCustom && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          自定义
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{info.description}</p>
                  </div>
                  {getStatusIndicator(config)}
                </div>

                {/* API Key 输入 */}
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={config.apiKey}
                    onChange={(e) => handleApiKeyChange(model, e.target.value)}
                    placeholder={info.placeholder}
                    className="input font-mono text-sm flex-1"
                  />
                  <button
                    onClick={() => handleTest(model)}
                    disabled={config.status === 'testing'}
                    className="btn btn-primary px-4 whitespace-nowrap"
                  >
                    {config.status === 'testing' ? '测试中...' : '测试'}
                  </button>
                  {config.apiKey && (
                    <button
                      onClick={() => handleClear(model)}
                      className="btn btn-secondary px-4"
                    >
                      清除
                    </button>
                  )}
                </div>

                {/* 提示信息 */}
                {config.status === 'success' && (
                  <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                    ✓ 此 API 已可用，可在主页选择使用{isUsingCustom ? '（使用您的自定义配置）' : '（使用后台默认配置）'}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* 使用说明 */}
      <div className="card bg-gray-50">
        <h3 className="font-semibold text-gray-900 mb-3">使用说明</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li><strong>推荐方式</strong>：留空 API 输入框，点击"测试"按钮验证后台默认配置</li>
          <li><strong>自定义方式</strong>：输入您自己的 API 密钥，点击"测试"验证密钥是否有效</li>
          <li>测试成功后，配置会自动保存到浏览器本地</li>
          <li>返回首页后，可以选择任意已配置的 AI 进行文献检索和题目生成</li>
          <li><strong>隐私保护</strong>：用户自定义的密钥仅保存在浏览器中，不会上传到服务器</li>
        </ol>
      </div>

      {/* 如何获取 API Key */}
      <div className="card bg-amber-50 border border-amber-200">
        <h3 className="font-semibold text-amber-900 mb-3">💰 如何获取 API Key？</h3>
        <div className="space-y-2 text-sm text-amber-800">
          <div><strong>OpenAI:</strong> 访问 platform.openai.com → API keys</div>
          <div><strong>DeepSeek:</strong> 访问 platform.deepseek.com → API keys</div>
          <div><strong>豆包:</strong> 访问 console.volcengine.com → Doubao → API 密钥</div>
          <div><strong>千问:</strong> 访问 dashscope.console.aliyun.com → API-KEY 管理</div>
        </div>
      </div>
    </div>
  )
}
